/**
 * Does the protocol enrol ONE sex only? (docgen E1, 2026-10-05)
 *
 * Read from the protocol reading (`population.gender`, else the inclusion criteria), never guessed: a study
 * is single-sex only when the reading says so plainly. "Female sexual partners of childbearing potential must
 * use contraception" is a criterion about partners, not an enrolment restriction, and "Male and female" or
 * "All" is both. When the restriction is not clearly stated the answer is null and nothing changes.
 *
 * Also: does the protocol have pregnancies in a participant's PARTNER reported? That is what a male-only
 * study collects in place of a pregnancy test.
 *
 * PURE. No Firestore, no model.
 */

export type SingleSex = "male" | "female";

const MALE_WORD = /^(?:male|males|men|man)$/i;
const FEMALE_WORD = /^(?:female|females|women|woman)$/i;

const textOf = (v: unknown): string =>
  typeof v === "string" ? v : v && typeof v === "object" ? String((v as { text?: unknown; criterion?: unknown }).text ?? (v as { criterion?: unknown }).criterion ?? "") : "";

/** A gender field read as one sex ("Male", "Females only", "Male participants"), or null. */
function fromGenderField(raw: unknown): SingleSex | null {
  const g = textOf(raw).trim().replace(/[.;]+$/, "").replace(/\s+(?:only|participants?|subjects?|patients?|volunteers|individuals)$/i, "").trim();
  if (MALE_WORD.test(g)) return "male";
  if (FEMALE_WORD.test(g)) return "female";
  return null;
}

/** A gender field that says both sexes. */
const BOTH_SEXES = /\b(?:both|all|any|male\s*(?:and|or|\/|,)\s*female|female\s*(?:and|or|\/|,)\s*male|men\s+and\s+women|women\s+and\s+men)\b/i;

/**
 * The criteria path is STRICT: a miss is safe (nothing changes), a false hit is harmful. Only two shapes restrict.
 *  (i) The whole criterion is a sex statement, anchored at both ends, with at most a strict age tail:
 *      "Males only", "Female participants only, aged 18 to 40", "Male only aged 18-55", "Only women aged 18 to 45."
 *  (ii) The sex is the subject of an eligibility verb: "Only women are eligible", "Only males will be enrolled ...".
 * Anything else ("Only women aged 18 to 45 need a pregnancy test", "a test for women only", "Only women who are
 * included in the sub-study ...") qualifies a procedure or a sub-group and enrols nobody.
 */
const SEX_WORD = "(males?|females?|men|women)(?:\\s+(?:participants?|subjects?|patients?|volunteers))?";
const AGE_TAIL = "(?:(?:\\s*,\\s*|\\s+)aged?\\s+\\d+(?:\\s*(?:to|-|–|and)\\s*\\d+)?(?:\\s*years?)?(?:\\s+(?:or older|inclusive))?)?";
const WHOLE_STATEMENT = [
  new RegExp(`^only\\s+${SEX_WORD}${AGE_TAIL}[.]?$`, "i"),
  new RegExp(`^${SEX_WORD}\\s+only${AGE_TAIL}[.]?$`, "i"),
];
const ELIGIBILITY_SUBJECT = new RegExp(`^only\\s+${SEX_WORD}\\s+(?:are|is|will be|shall be)\\s+(?:eligible|enrolled)(?:\\s+(?:in|into|for|to)\\s+(?:this|the)\\s+(?:study|trial))?${AGE_TAIL}[.]?$`, "i");

function enrolmentSex(t: string): string | null {
  for (const re of [...WHOLE_STATEMENT, ELIGIBILITY_SUBJECT]) {
    const m = re.exec(t);
    if (m) return m[1];
  }
  return null;
}

/** What an inclusion criterion says about the sex enrolled, or null. */
function fromCriterion(text: string): SingleSex | null {
  const t = text.replace(/\s+/g, " ").trim();
  if (!t) return null;
  // A condition on the restriction is not a restriction ("Males only if vasectomised").
  if (/\b(?:only\s+if|unless|provided\s+that|if\s+(?:vasectomi|sterili))/i.test(t)) return null;
  // Only an EXPLICIT statement counts: "<sex> only", "only <sex>", "sex assigned at birth: <sex>", "<sex> sex at birth".
  // A criterion that merely begins with a sex ("Women aged 18 to 45 with ...") or mentions "female sex" is not one.
  const sexAtBirth = /^(male|female)\s+sex\s+(?:assigned\s+)?at\s+birth\b/i.exec(t) ?? /^(?:sex|gender)(?:\s+assigned\s+at\s+birth)?\s*[:=-]\s*(male|female)\b/i.exec(t);
  if (sexAtBirth) return sexAtBirth[1].toLowerCase() as SingleSex;
  const w = enrolmentSex(t);
  if (w) return MALE_WORD.test(w.toLowerCase()) ? "male" : "female";
  return null;
}

const MENTIONS: Record<SingleSex, RegExp> = {
  // The other sex as a participant. "of childbearing potential" counts as a mention of female participants.
  male: /\b(?:women|woman|females?)\b|\bchild-?bearing\b|\bpregnan|\bbreast-?feeding\b|\b(?:post|pre)-?menopaus/i,
  female: /\b(?:men|man|males?)\b/i,
};

/** Does any criterion talk about the OTHER sex as a participant? A sentence about a partner is about the partner. */
function mentionsOtherSex(sex: SingleSex, criteria: string[]): boolean {
  const other = sex === "male" ? MENTIONS.male : MENTIONS.female;
  return criteria.some((c) => !/\b(?:partners?|spouses?)\b/i.test(c) && other.test(c.replace(/\b(?:male|female)\s+sex\s+(?:assigned\s+)?at\s+birth\b/gi, "")));
}

/** The one sex the protocol enrols, or null when it enrols both or does not say. */
export function singleSexRestriction(analysis: Record<string, unknown> | null | undefined): SingleSex | null {
  const pop = (analysis as { population?: Record<string, unknown> } | null | undefined)?.population;
  if (!pop || typeof pop !== "object") return null;
  const gender = textOf(pop.gender);
  if (gender) {
    if (BOTH_SEXES.test(gender)) return null;
    const g = fromGenderField(gender);
    if (g) return g;
  }
  const inclusion = Array.isArray(pop.inclusion) ? (pop.inclusion as unknown[]) : [];
  const exclusion = Array.isArray(pop.exclusion) ? (pop.exclusion as unknown[]) : [];
  const found = new Set<SingleSex>();
  for (const c of inclusion) {
    const s = fromCriterion(textOf(c));
    if (s) found.add(s);
  }
  if (found.size !== 1) return null;
  const sex = [...found][0];
  // The statement stands only when no criterion treats the other sex as a participant. When in doubt: null.
  return mentionsOtherSex(sex, [...inclusion, ...exclusion].map(textOf)) ? null : sex;
}

/** The subject's own childbearing potential is not collected in a male-only study; a partner's is. */
const SUBJECT_CBP = /child-?bearing|reproductive potential|WOCBP/i;
/**
 * The SUBJECT's own pregnancy test or pregnancy / lactation status (docgen E2): "Was a pregnancy test performed?",
 * "Test result", "Is the participant pregnant?", "Lactating", "Serum hCG". Matched on the question text, or on a
 * PREG-prefixed variable name (the follow-on fields of a test read "Date of test", "Test type", "Test result" and
 * say nothing about pregnancy). A pregnancy OUTCOME or a partner's pregnancy is not a subject test.
 */
const PREG_TEST_TEXT = /pregnan\w*\s+(?:test|testing|status|result|screen)|\b(?:urine|serum)\s+pregnan|\bhcg\b|\bis\b[^?]*\bpregnant\b|\bpregnant\b|\blactat|\bbreast-?feed|\bnursing\b/i;
/** A variable name of a subject's pregnancy test or status field: PREG*, PRG*, HCG*, UPT*. */
const PREG_NAME = /^(?:PREG|PRG|HCG|UPT)/i;
/** Pregnancy follow-up wording, a neonate, an infant or the mother: never the participant's own test or status. */
const NOT_SUBJECT_STATUS = /\boutcomes?\b|\bdeliver|\bEDD\b|\bnotif|\bconsent|\baware|\bterminat|\bfollow-?up\b|\binfants?\b|\bneonat\w*|\bbab(?:y|ies)\b|\bnewborn|\bmother|\bchild(?:ren)?\b/i;
const INFANT_WORDS = /\binfants?\b|\bneonat\w*|\bbab(?:y|ies)\b|\bnewborn|\bmother|\bchild(?:ren)?\b/i;
const PARTNER_WORDS = /\b(?:partners?|spouses?|wife|wives|girlfriends?|female sexual)\b|\b(?:her|his)\s+(?:\w+\s+)?partner|PARTCBP/i;
const PARTNER_FORM_NAME = /^(?:PP|PART)/i;
/**
 * A form that is ABOUT a pregnancy report or a partner (a partner form, "Pregnancy Reporting", "Pregnancy Outcome",
 * "Pregnancy Follow-up", "Pregnancy Exposure", "Pregnancy Notification") keeps all its pregnancy fields: that is
 * where a reported pregnancy's outcome, delivery date and notification live. A form named for a TEST does not.
 */
const KEEP_FORM = /\b(?:partners?|spouses?|wife|wives)\b|pregnan\w*[^,;]*\b(?:report\w*|outcomes?|notif\w*|exposure|follow-?up)\b|\b(?:report\w*|outcomes?|notif\w*|exposure|follow-?up)\b[^,;]*pregnan/i;
const keepForm = (formName?: string | null): boolean => !!formName && KEEP_FORM.test(formName) && !/\btest/i.test(formName);

export function dropSubjectChildbearingFields<V extends { varName?: string; questionText?: string }>(variables: V[], analysis: Record<string, unknown> | null | undefined, formName?: string | null): V[] {
  if (singleSexRestriction(analysis) !== "male") return variables;
  const inKeepForm = keepForm(formName);
  return variables.filter((v) => {
    const q = v.questionText ?? "";
    const text = `${q} ${v.varName ?? ""}`;
    if (PARTNER_WORDS.test(text) || PARTNER_FORM_NAME.test(v.varName ?? "")) return true;
    if (SUBJECT_CBP.test(text)) return false;
    if (/contracept/i.test(text) || INFANT_WORDS.test(q) || inKeepForm) return true;
    // A test or hCG phrase beats the keep words: "Was a pregnancy test performed at follow-up?" is still the subject's test.
    if (PREG_TEST_TEXT.test(q)) return false;
    if (NOT_SUBJECT_STATUS.test(q)) return true;
    return !PREG_NAME.test(v.varName ?? "");
  });
}

const HITS_OPTION: Record<SingleSex, RegExp> = { male: /^(?:male|man|men)\b/i, female: /^(?:female|woman|women)\b/i };

/**
 * A sex list reduced to the one sex's own option, with the code it already carries (the sponsor's, the
 * protocol's or CDASH's). Returned unchanged when the list has no option for that sex.
 */
export function restrictSexOptions<O extends { code?: number | string; label: string }>(options: O[], sex: SingleSex | null): O[] {
  if (!sex) return options;
  const kept = options.filter((o) => HITS_OPTION[sex].test(String(o.label).trim()));
  return kept.length > 0 ? kept : options;
}

/** Pregnancies in the participant's partner are to be reported or collected (the male-only study's pregnancy requirement). */
export function partnerPregnancyReported(analysis: Record<string, unknown> | null | undefined): boolean {
  let found = false;
  const walk = (v: unknown, depth: number): void => {
    if (found || depth > 6 || v == null) return;
    if (typeof v === "string") {
      for (const sentence of v.split(/(?<=[.!?;])\s+/)) {
        if (/pregnan/i.test(sentence) && /\b(?:partners?|spouses?)\b/i.test(sentence) && /report|collect|follow|notif|monitor|obtain|record/i.test(sentence)) { found = true; return; }
      }
      return;
    }
    if (Array.isArray(v)) { for (const x of v) walk(x, depth + 1); return; }
    if (typeof v === "object") for (const x of Object.values(v as Record<string, unknown>)) walk(x, depth + 1);
  };
  walk(analysis, 0);
  return found;
}
